import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CfInfoDialog from "@/components/pop-ui/cf-info-dialog";
import { toast } from "@/components/pop-ui/pop-toast";
import { PasswordInput } from "@/components/ui/password-input";
import { handleAuthCall } from "@/lib/auth/auth-utils";
import { getAuthHeadersWaitSign } from "@/lib/auth/td-utils";
import { useGlobalConfig } from "@/providers/global-config-context";
import { useTurnstile } from "@/providers/turnstile-provider";
import { authClient } from "../../../lib/auth/auth-client";
import { CodeInput, type CodeInputRef } from "../components/code-input";
import { Button, Input } from "../components/form-items";
import { handleAuthApiError, type UserFormErrors, validatePassword } from "../utils";
interface ResetFormEmailProps {
  inputValue: string;
  area: string;
  onFinish?: () => void;
}
export function ResetFormEmail(t0) {
  const $ = _c(90);
  const {
    inputValue,
    onFinish
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    globalConfig
  } = useGlobalConfig();
  const {
    isSdkReady,
    sdkError
  } = useTurnstile();
  const emailId = useId();
  const newPasswordId = useId();
  const [newPassword, setNewPassword] = useState("");
  const [code, setCode] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const timeoutRef = useRef(null);
  const codeInputRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      area: "",
      email: "",
      password: "",
      code: ""
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [errors, setErrors] = useState(t1);
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [isTurnstileProcessStarted, setIsTurnstileProcessStarted] = useState(false);
  const handleTurnstileSuccess = token => {
    setCaptchaOpen(false);
    sendResetOTP(token);
  };
  let t2;
  if ($[1] !== isSdkReady || $[2] !== sdkError || $[3] !== t) {
    t2 = () => {
      if (sdkError) {
        toast.error(t("gateway.error.475"));
        return;
      }
      if (!isSdkReady) {
        return;
      }
      setIsTurnstileProcessStarted(true);
      setCaptchaOpen(true);
    };
    $[1] = isSdkReady;
    $[2] = sdkError;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleTurnstileOpen = t2;
  let t3;
  if ($[5] !== t) {
    t3 = () => {
      setIsTurnstileProcessStarted(false);
      setCaptchaOpen(false);
      toast.error(t("gateway.error.476"));
    };
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleTurnstileError = t3;
  let t4;
  if ($[7] !== inputValue || $[8] !== t) {
    t4 = async cfTurnstileToken => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsTimedOut(true);
        toast.error(t("toast.error.requestTimeout", "Request timeout"));
      }, 30000);
      ;
      try {
        const requestHeaders = await getAuthHeadersWaitSign("email-otp/reset-password", {
          "x-captcha-response": cfTurnstileToken
        });
        const result = await handleAuthCall(authClient.emailOtp.sendVerificationOtp({
          email: inputValue,
          type: "forget-password",
          fetchOptions: {
            headers: requestHeaders
          }
        }));
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return result;
      } catch (t5) {
        const error = t5;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        throw error;
      }
    };
    $[7] = inputValue;
    $[8] = t;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  let t6;
  if ($[10] !== t) {
    t5 = () => {
      setShowCodeInput(true);
      toast.success(t("toast.success.verificationCodeSentToYourEmail"));
      setTimeout(() => {
        codeInputRef.current?.startCountdown();
      }, 500);
    };
    t6 = error_0 => {
      setCaptchaOpen(false);
      handleAuthApiError(error_0, t("toast.error.sendCodeFailedTryLater"));
    };
    $[10] = t;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = {
      mutationFn: t4,
      onSuccess: t5,
      onError: t6
    };
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const {
    mutate: t8,
    isPending: isSendingOTP
  } = useMutation(t7);
  const sendResetOTP = t8;
  let t9;
  if ($[17] !== code || $[18] !== inputValue || $[19] !== newPassword || $[20] !== t) {
    t9 = async () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setIsTimedOut(true);
        toast.error(t("toast.error.requestTimeout", "Request timeout"));
      }, 30000);
      ;
      try {
        const requestHeaders_0 = await getAuthHeadersWaitSign("email-otp/reset-password", {});
        const result_0 = await handleAuthCall(authClient.emailOtp.resetPassword({
          email: inputValue,
          otp: code,
          password: newPassword,
          fetchOptions: {
            headers: requestHeaders_0
          }
        }));
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        return result_0;
      } catch (t10) {
        const error_1 = t10;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        throw error_1;
      }
    };
    $[17] = code;
    $[18] = inputValue;
    $[19] = newPassword;
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== onFinish || $[23] !== t) {
    t10 = () => {
      toast.success(t("toast.success.passwordResetSuccess"));
      if (onFinish) {
        onFinish();
      }
    };
    $[22] = onFinish;
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t) {
    t11 = error_2 => {
      setCaptchaOpen(false);
      handleAuthApiError(error_2, t("toast.error.resetPasswordFailedTryLater"));
    };
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t10 || $[28] !== t11 || $[29] !== t9) {
    t12 = {
      mutationFn: t9,
      onSuccess: t10,
      onError: t11
    };
    $[27] = t10;
    $[28] = t11;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const {
    mutate: resetPassword,
    isPending: isResetting
  } = useMutation(t12);
  let t13;
  if ($[31] !== globalConfig?.PUBLIC_TURNSTILE_ENABLED || $[32] !== handleTurnstileOpen || $[33] !== newPassword || $[34] !== sendResetOTP || $[35] !== t) {
    t13 = async () => {
      setIsTimedOut(false);
      setErrors({
        area: "",
        email: "",
        phone: "",
        password: "",
        code: ""
      });
      const passwordError = validatePassword(newPassword, t);
      if (passwordError) {
        setErrors(prev => ({
          ...prev,
          password: passwordError
        }));
        return;
      }
      if (!globalConfig?.PUBLIC_TURNSTILE_ENABLED) {
        sendResetOTP("");
        return;
      }
      handleTurnstileOpen();
    };
    $[31] = globalConfig?.PUBLIC_TURNSTILE_ENABLED;
    $[32] = handleTurnstileOpen;
    $[33] = newPassword;
    $[34] = sendResetOTP;
    $[35] = t;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  const handleResendOTP = t13;
  let t14;
  if ($[37] !== globalConfig?.PUBLIC_TURNSTILE_ENABLED || $[38] !== handleTurnstileOpen || $[39] !== isTurnstileProcessStarted || $[40] !== newPassword || $[41] !== sendResetOTP || $[42] !== t) {
    t14 = async e => {
      e.preventDefault();
      if (isTurnstileProcessStarted) {
        return;
      }
      setIsTimedOut(false);
      setErrors({
        area: "",
        email: "",
        phone: "",
        password: "",
        code: ""
      });
      const passwordError_0 = validatePassword(newPassword, t);
      if (passwordError_0) {
        setErrors(prev_0 => ({
          ...prev_0,
          password: passwordError_0
        }));
        return;
      }
      if (!globalConfig?.PUBLIC_TURNSTILE_ENABLED) {
        sendResetOTP("");
        return;
      }
      handleTurnstileOpen();
    };
    $[37] = globalConfig?.PUBLIC_TURNSTILE_ENABLED;
    $[38] = handleTurnstileOpen;
    $[39] = isTurnstileProcessStarted;
    $[40] = newPassword;
    $[41] = sendResetOTP;
    $[42] = t;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  const handleSendOTP = t14;
  let t15;
  if ($[44] !== code || $[45] !== newPassword || $[46] !== resetPassword || $[47] !== t) {
    t15 = async e_0 => {
      e_0.preventDefault();
      setIsTimedOut(false);
      const passwordError_1 = validatePassword(newPassword, t);
      if (passwordError_1) {
        setErrors(prev_1 => ({
          ...prev_1,
          password: passwordError_1
        }));
        return;
      }
      if (!code) {
        setErrors(prev_2 => ({
          ...prev_2,
          code: t("toast.error.enterVerificationCode")
        }));
        return;
      } else {
        if (code.length !== 6) {
          setErrors(prev_3 => ({
            ...prev_3,
            code: t("toast.error.enter6DigitVerificationCode")
          }));
          return;
        }
      }
      resetPassword();
    };
    $[44] = code;
    $[45] = newPassword;
    $[46] = resetPassword;
    $[47] = t;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  const handleResetPassword = t15;
  const isLoading = (isSendingOTP || isResetting) && !isTimedOut;
  const t16 = showCodeInput ? handleResetPassword : handleSendOTP;
  let t17;
  if ($[49] !== emailId || $[50] !== inputValue) {
    t17 = <Input id={emailId} type="email" value={inputValue} disabled={true} className="bg-gray-100" />;
    $[49] = emailId;
    $[50] = inputValue;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== errors.email) {
    t18 = errors.email && <p className="text-destructive text-sm leading-[1.5]">{errors.email}</p>;
    $[52] = errors.email;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== t17 || $[55] !== t18) {
    t19 = <div className="space-y-2">{t17}{t18}</div>;
    $[54] = t17;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== t) {
    t20 = t("placeholder.setNewPassword");
    $[57] = t;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = e_1 => setNewPassword(e_1.target.value);
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== errors.password || $[61] !== isLoading || $[62] !== newPassword || $[63] !== newPasswordId || $[64] !== t20) {
    t22 = <div className="space-y-2"><PasswordInput id={newPasswordId} placeholder={t20} value={newPassword} onChange={t21} disabled={isLoading} error={errors.password} /></div>;
    $[60] = errors.password;
    $[61] = isLoading;
    $[62] = newPassword;
    $[63] = newPasswordId;
    $[64] = t20;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== code || $[67] !== errors.code || $[68] !== handleResendOTP || $[69] !== isLoading || $[70] !== showCodeInput) {
    t23 = showCodeInput && <div className="space-y-2"><CodeInput ref={codeInputRef} value={code} onChange={setCode} error={errors.code} onResend={handleResendOTP} disabled={isLoading} /></div>;
    $[66] = code;
    $[67] = errors.code;
    $[68] = handleResendOTP;
    $[69] = isLoading;
    $[70] = showCodeInput;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  let t24;
  if ($[72] !== captchaOpen || $[73] !== handleTurnstileError || $[74] !== handleTurnstileSuccess) {
    t24 = <CfInfoDialog open={captchaOpen} onOpenChange={setCaptchaOpen} containerId="cf-turnstile-container-reset-dialog" onTurnstileSuccess={handleTurnstileSuccess} onTurnstileError={handleTurnstileError} />;
    $[72] = captchaOpen;
    $[73] = handleTurnstileError;
    $[74] = handleTurnstileSuccess;
    $[75] = t24;
  } else {
    t24 = $[75];
  }
  let t25;
  if ($[76] !== isLoading || $[77] !== showCodeInput || $[78] !== t) {
    t25 = isLoading ? showCodeInput ? t("button.resetting") : t("button.sending") : showCodeInput ? t("button.resetPassword") : t("button.sendCode");
    $[76] = isLoading;
    $[77] = showCodeInput;
    $[78] = t;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  let t26;
  if ($[80] !== isLoading || $[81] !== t25) {
    t26 = <Button type="submit" className="w-full" size="lg" loading={isLoading}>{t25}</Button>;
    $[80] = isLoading;
    $[81] = t25;
    $[82] = t26;
  } else {
    t26 = $[82];
  }
  let t27;
  if ($[83] !== t16 || $[84] !== t19 || $[85] !== t22 || $[86] !== t23 || $[87] !== t24 || $[88] !== t26) {
    t27 = <form onSubmit={t16} className="space-y-3">{t19}{t22}{t23}{t24}{t26}</form>;
    $[83] = t16;
    $[84] = t19;
    $[85] = t22;
    $[86] = t23;
    $[87] = t24;
    $[88] = t26;
    $[89] = t27;
  } else {
    t27 = $[89];
  }
  return t27;
}